import { useState, useRef, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Ship,
  Package,
  Truck,
  Warehouse,
  Globe,
  Anchor,
  Shield,
  Box,
  Cpu,
} from "lucide-react";

const services = [
  {
    title: "Shipping Agency / Steamer Agents",
    icon: Ship,
    color: "primary",
  },
  {
    title: "Customs House Agent (CHA) ",
    icon: Shield,
    color: "primary",
  },
  {
    title: "Cargo Handling: Bulk, Breakbulk, Containerized, and Project Cargo",
    icon: Package,
    color: "primary",
  },
  {
    title: "Cargo Equipment Provision",
    icon: Cpu,
    color: "primary",
  },
  {
    title: "Tools and Tackles Supply",
    icon: Box,
    color: "primary",
  },
  {
    title: "Transportation of Bulk Materials",
    icon: Truck,
    color: "primary",
  },
  {
    title: "Ship Store and Provision Supply",
    icon: Warehouse,
    color: "primary",
  },
  {
    title: "Cross-Functional Supply Chain Management for Multiple Cargo",
    icon: Globe,
    color: "primary",
  },
  {
    title: "Rake, Road, and Inland Waterways Cargo Movement",
    icon: Anchor,
    color: "primary",
  },
];

const colorClasses = {
  primary: "bg-primary/10 text-primary border-primary/10",
};

export default function ProfessionalServicesSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const sliderRef = useRef(null);

  const itemsPerSlide = 3;
  const totalSlides = Math.ceil(services.length / itemsPerSlide);
  const minSwipeDistance = 50;

  const handleSlideChange = (direction) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) =>
      direction === "next"
        ? (prev + 1) % totalSlides
        : (prev - 1 + totalSlides) % totalSlides
    );
    setTimeout(() => setIsAnimating(false), 300);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || isAnimating) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) handleSlideChange("next");
    if (isRightSwipe) handleSlideChange("prev");
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) handleSlideChange("next");
    }, 6000);
    return () => clearInterval(interval);
  }, [currentSlide, isAnimating]);

  const getVisibleServices = () => {
    const start = currentSlide * itemsPerSlide;
    return services.slice(start, start + itemsPerSlide);
  };

  return (
    <section className="py-8 md:py-10">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-[24px] font-TuskerGrotesk font-black text-black mb-4 uppercase leading-[1.1] tracking-wider">
              Our <span className="text-primary italic">Services</span>
            </h2>
          </div>

          {/* Slider */}
          <div
            ref={sliderRef}
            className="relative"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Desktop Navigation */}
            <button
              onClick={() => handleSlideChange("prev")}
              className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-8 z-20 w-10 h-10  transition-all hover:scale-105"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700 mx-auto" />
            </button>

            <button
              onClick={() => handleSlideChange("next")}
              className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-8 z-20 w-10 h-10  transition-all hover:scale-105"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5 text-gray-700 mx-auto" />
            </button>

            {/* Mobile Navigation */}
            <div className="flex justify-end mb-4 lg:hidden">
              <div className="flex space-x-2">
                <button
                  onClick={() => handleSlideChange("prev")}
                  className="w-8 h-8 rounded-full bg-white shadow-sm hover:shadow transition-shadow"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-700 mx-auto" />
                </button>
                <button
                  onClick={() => handleSlideChange("next")}
                  className="w-8 h-8 rounded-full bg-white shadow-sm hover:shadow transition-shadow"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-4 h-4 text-gray-700 mx-auto" />
                </button>
              </div>
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              {getVisibleServices().map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={index}
                    className={`bg-white/95 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 p-6 md:p-8 border border-white/20 group/card min-h-[280px] ${isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"
                      }`}
                  >
                    <div className="flex flex-col h-full items-center text-center relative pt-4">

                      {/* Slide Number (Positioned Top Right) */}
                      <div className="absolute -top-2 right-0">
                        <span className="text-[40px] font-TuskerGrotesk font-black text-primary/10 group-hover/card:text-primary/20 transition-colors uppercase tracking-widest">
                          {(currentSlide * itemsPerSlide + index + 1).toString().padStart(2, "0")}
                        </span>
                      </div>

                      {/* Large Centered Icon Section */}
                      <div className="mt-auto z-10">
                        <div className={`p-5 md:p-6 rounded-full inline-flex ${colorClasses[service.color]} border border-primary/20 group-hover/card:bg-primary group-hover/card:text-white transition-all duration-500 hover:scale-110 shadow-sm`}>
                          <Icon className="w-12 h-12 md:w-16 md:h-16" strokeWidth={1.5} />
                        </div>
                      </div>

                      {/* Title Section */}
                      <div className="flex-grow flex items-center justify-center w-full mb-8 z-10 mt-4">
                        <h3 className="text-[14px] font-bold text-gray-900 leading-snug">
                          {service.title}
                        </h3>
                      </div>



                    </div>
                  </div>
                );
              })}
            </div>

            {/* Progress Indicators */}
            <div className="mt-12">
              <div className="flex justify-center space-x-3">
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`rounded-full transition-all duration-500 ${index === currentSlide
                      ? "w-12 h-2.5 bg-primary shadow-lg shadow-primary/20"
                      : "w-2.5 h-2.5 bg-slate-200 hover:bg-slate-300"
                      }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}